import React, { useMemo, useCallback, useRef, useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "./AgGrid.css";

import PropTypes from "prop-types";
import moment from "moment";

import {
  Box,
  Stack,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  alpha,
  useTheme,
  Popover,
  Checkbox,
  Divider,
  Typography,
} from "@mui/material";

import {
  Search,
  Clear,
  FileDownload,
  ViewColumn,
  RestoreFromTrash,
  FilterAltOff,
  FilterAlt,
  Refresh,
  Add,
  BookmarkBorder,
  KeyboardArrowDown,
} from "@mui/icons-material";

import { Message, useToaster } from "rsuite";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";

// ─── Navigation routing map ──────────────────────────────────────────────────
// Centralises the cell-click routing logic for all pages
const getCellClickDestination = ({ GoTo, Source, event, handleViewModalOpen, navigate }) => {
  const { colDef, node } = event;
  const field = colDef.field;
  const data = node.data;
  const selected = node.isSelected();

  // Helper shortcuts
  const goTo = (path) => navigate(path);
  const viewModal = () => handleViewModalOpen?.();

  if (GoTo && (field === "ID" || field === "Reference")) {
    if (!selected) return;
    if (Source === "Followup" || Source === "FollowupMenu") {
      const isCLIA =
        data?.Type === "CLIAAgencyData" ||
        String(data?.Reference || data?.ID || "")
          .toUpperCase()
          .startsWith("AGENT");
      return isCLIA
        ? goTo(`/CLIAProspect/EditCLIAProspect/${data?.ProspectID}`)
        : goTo(`/Prospects/ViewProspect/${data?.ProspectID}`);
    }
    return goTo(`${GoTo}/${data?.UniqueID}`);
  }

  if (field === "ProspectID") return viewModal();
  if (field === "Name" && Source === "Family") return viewModal();

  if (Source === "PremiumPoint") {
    return field === "Reference" && data?.ReferenceID !== null
      ? goTo(`/Prospects/ViewProspect/${data?.ReferenceID}`)
      : viewModal();
  }

  if (Source === "PastePremiumPointNew") {
    return viewModal();
  }

  const modalOnlySources = [
    "CurrentMonthDue", "OutstandingDueList", "LICMaturityList",
    "SBPolicyList", "PastePremiumPoint", "LapsedPolicyDue",
  ];
  if (modalOnlySources.includes(Source)) return viewModal();

  if (GoTo && field === "FirstName" && Source === "User" && selected)
    return goTo(`${GoTo}/${data?.UniqueID}`);

  if (GoTo && (field === "fullname" || field === "ID") && (Source === "DahiyaMitra" || Source === "CLIA") && selected)
    return goTo(`${GoTo}/${data?.UniqueID}`);

  if (GoTo && Source === "Policy" && selected) {
    if (field === "RefID") return goTo(`/Prospects/ViewProspect/${data?.ReferenceID}`);
    if (field === "PolicyNo") return goTo(`${GoTo}/${data?.UniqueID}`);
  }

  const cliaRefSources = ["CLIAReference", "CLIAProspect", "CLIAAgencyData"];
  if (GoTo && cliaRefSources.includes(Source) && selected) {
    if (field === "RefID") return goTo(`${GoTo}/${data?.UniqueID}`);
  }

  const servicesSources = ["Bond", "ENach", "MinorDAB", "MinorNomination"];
  if (GoTo && servicesSources.includes(Source) && selected) {
    if (field === "RefID") return goTo(`/Prospects/ViewProspect/${data?.ReferenceID}`);
    if (field === "PolicyNo") return goTo(`${GoTo}/${data?.UniqueID}`);
  }

  if (GoTo && Source === "PolicyService" && selected) {
    if (field === "Reference") return goTo(`/Prospects/ViewProspect/${data?.ReferenceID}`);
    if (field === "PolicyNo" && data?.PolicyNoID !== "00000000-0000-0000-0000-000000000000")
      return goTo(`/Policy/View/${data?.PolicyNoID}`);
    if (field === "ServiceID") return goTo(`${GoTo}/${data?.UniqueID}`);
  }

  if (Source === "Followup" || Source === "FollowupMenu" || Source === "LICInvoice") {
    if (field === "Reference" || field === "ID") {
      const isCLIA =
        data?.Type === "CLIAAgencyData" ||
        String(data?.Reference || data?.ID || "")
          .toUpperCase()
          .startsWith("AGENT");
      return isCLIA
        ? goTo(`/CLIAProspect/EditCLIAProspect/${data?.ProspectID}`)
        : goTo(`/Prospects/ViewProspect/${data?.ProspectID}`);
    }
    if (["Result", "DiscussionCount", "InvoiceID"].includes(field)) return viewModal();
  }
};

// ─── Main Component ──────────────────────────────────────────────────────────

export default function AgGrid({
  columns = [
    { headerName: "Athlete", field: "athlete", minWidth: 180 },
    { field: "age" },
    { field: "country", minWidth: 150 },
    { field: "year" },
    { field: "date", minWidth: 150 },
    { field: "sport", minWidth: 150 },
    { field: "gold" },
    { field: "silver" },
    { field: "bronze" },
    { field: "total", hide: true },
  ],
  rowData = [],
  onGridReady,
  ExportFileName = `${moment().format("DDMMYYYYHHmmss")}.csv`,
  GoTo = "",
  showHeaderItems = true,
  passdata = false,
  QuickSearch = true,
  exportAllowed = true,
  ChooseColumns = true,
  showLoading = false,
  addMember = false,
  addDiscussion = false,
  handleOpen,
  onRowSelectedData,
  handleViewModalOpen,
  onDataChange,
  headerComponent,
  setFilteredRows,
  Source = "Followup",
  settingsName,
  theme = "ag-theme-alpine",
  setRowStyle = false,
  height,
}) {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === "dark";
  const gridRef = useRef(null);
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const toaster = useToaster();

  // State: Columns & Definitions
  const [selectedColumns, setSelectedColumns] = useState(() => {
    return columns.filter((c) => !c.hide).map((c) => c.field);
  });
  const [columnDefs, setColumnDefs] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [settings, setSettings] = useState([]);
  const [filteredRowNodes, setFilteredRowNodes] = useState([]);
  const [currentRowIndex, setCurrentRowIndex] = useState(0);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  // Column Popover State
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [columnSearch, setColumnSearch] = useState("");

  const handleColumnsOpen = (e) => setColumnsAnchor(e.currentTarget);
  const handleColumnsClose = () => {
    setColumnsAnchor(null);
    setColumnSearch("");
  };

  // ── Grid Configuration ──────────────────────────────────────────────────────

  const defaultColDef = useMemo(() => ({
    flex: 1,
    editable: false,
    filter: true,
    minWidth: 140,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    enableCellChangeFlash: true,
    resizable: true,
    sortable: true,
    headerComponentParams: { menuIcon: "fa-bars" },
  }), []);

  const gridOptions = useMemo(() => ({
    getRowId: (params) => params.data?.UniqueID,
    enableCellTextSelection: true,
    enableBrowserTooltips: true,
    suppressCellFocus: false,
    animateRows: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [20, 50, 100, 200],
  }), []);

  const autoSizeStrategy = useMemo(() => ({ type: "fitGridWidth" }), []);

  const getRowStyle = useCallback((params) => {
    if (!setRowStyle) return null;
    if (params.data?.Type === "Credit") return { color: "#16a34a", fontWeight: 600 };
    if (params.data?.Type === "Debit") return { color: "#dc2626", fontWeight: 600 };
    return null;
  }, [setRowStyle]);

  // ── Filtered row tracking ───────────────────────────────────────────────────

  const updateFilteredRowNodes = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const nodes = [];
    api.forEachNodeAfterFilterAndSort((node) => {
      if (!node.group) nodes.push(node);
    });
    setFilteredRowNodes(nodes);
    setFilteredRows?.(nodes);
    setCurrentRowIndex(nodes.length > 0 ? 0 : -1);

    const isGridFiltered = api.isAnyFilterPresent ? api.isAnyFilterPresent() : false;
    setHasActiveFilters(Boolean(searchText.trim()) || isGridFiltered);
  }, [setFilteredRows, searchText]);

  useEffect(() => {
    if (gridRef.current && rowData?.length > 0) {
      updateFilteredRowNodes();
    }
  }, [rowData, updateFilteredRowNodes]);

  useEffect(() => {
    if (!gridRef.current || filteredRowNodes.length === 0) return;
    const node = filteredRowNodes[currentRowIndex];
    if (node) {
      gridRef.current.api.ensureIndexVisible(node.rowIndex);
      node.setSelected(true);
    }
  }, [currentRowIndex, filteredRowNodes]);

  // ── Loading overlay ─────────────────────────────────────────────────────────

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (showLoading) api.showLoadingOverlay();
    else api.hideOverlay();
  }, [showLoading]);

  // ── Column visibility from server settings ──────────────────────────────────

  useEffect(() => {
    if (!Source) return;
    axiosPrivate
      .post("GetSettings.php", { Source })
      .then((response) => {
        const result = response.data;
        if (result.status === 1 && Array.isArray(result.Columns) && result.Columns.length > 0) {
          const matched = columns.filter((c) => result.Columns.includes(c.field));
          if (matched.length > 0) {
            setSelectedColumns(result.Columns);
            setColumnDefs(matched);
          }
        }
      })
      .catch((e) => console.error("Failed to load grid settings:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Source]);

  // Keep column defs in sync when column selection changes
  useEffect(() => {
    if (selectedColumns && selectedColumns.length > 0) {
      const filtered = columns.filter((c) => selectedColumns.includes(c.field));
      if (filtered.length > 0) {
        setColumnDefs(filtered);
      }
    }
  }, [selectedColumns, columns]);

  // ── Cell click routing ──────────────────────────────────────────────────────

  const onRowSelected = useCallback((event) => {
    getCellClickDestination({ GoTo, Source, event, handleViewModalOpen, navigate });
    onRowSelectedData?.(event.node.data);
  }, [GoTo, Source, navigate, handleViewModalOpen, onRowSelectedData]);

  // ── Quick filter ────────────────────────────────────────────────────────────

  const onQuickFilterChanged = useCallback((e) => {
    const val = e.target.value;
    setSearchText(val);
    gridRef.current?.api?.setGridOption("quickFilterText", val);
    setHasActiveFilters(
      Boolean(val.trim()) || (gridRef.current?.api?.isAnyFilterPresent ? gridRef.current.api.isAnyFilterPresent() : false)
    );
  }, []);

  const clearQuickSearch = useCallback(() => {
    setSearchText("");
    gridRef.current?.api?.setGridOption("quickFilterText", "");
    setHasActiveFilters(
      gridRef.current?.api?.isAnyFilterPresent ? gridRef.current.api.isAnyFilterPresent() : false
    );
  }, []);

  // ── Reset all filters ───────────────────────────────────────────────────────

  const resetFilters = useCallback(() => {
    const api = gridRef.current?.api;
    if (api) {
      api.setFilterModel(null);
      api.setGridOption("quickFilterText", "");
    }
    setSearchText("");
    setHasActiveFilters(false);
  }, []);

  // ── Cell value change (inline edit) ────────────────────────────────────────

  const onCellValueChanged = useCallback((event) => {
    const { colDef, data } = event;
    if ((colDef.field === "Status" || colDef.field === "LeadStatus") && Source === "Prospect") {
      onDataChange?.({
        UID: data.UniqueID, Status: data.Status,
        LeadStatus: data.LeadStatus, Type: Source, Data: "Status",
      });
    }
    if (colDef.field === "LeadStatus" && (Source === "Followup" || Source === "FollowupMenu")) {
      onDataChange?.({
        UID: data.ProspectID, LeadStatus: data.LeadStatus,
        Type: Source, Data: "LeadStatus",
      });
    }
  }, [Source, onDataChange]);

  // ── Settings persistence ────────────────────────────────────────────────────

  const pushToast = useCallback((type, message) => {
    toaster.push(
      <Message type={type} bordered showIcon>
        <strong>{type === "success" ? "Success!" : "Notice"}</strong> {message}
      </Message>,
      { placement: "topEnd", duration: 2500 }
    );
  }, [toaster]);

  const SaveSettings = useCallback(async () => {
    if (!gridRef.current) return;
    const api = gridRef.current.api;
    const columnState = (api?.getColumnState ? api.getColumnState() : gridRef.current.columnApi?.getColumnState()) || [];
    const settingsPayload = JSON.stringify({ columnState });
    try {
      const response = await axiosPrivate.post("SaveSettings.php", {
        Columns: selectedColumns,
        Source,
        Settings: settingsPayload,
      });
      const result = response.data;
      pushToast(result.status === 1 ? "success" : "error",
        result.status === 1 ? (result.Success || "Grid view layout saved successfully.") : "Error saving settings.");
    } catch (e) {
      console.error("Error saving settings:", e);
      pushToast("error", "Error saving settings.");
    }
  }, [axiosPrivate, selectedColumns, Source, pushToast]);

  const deleteSettings = useCallback(async () => {
    try {
      const response = await axiosPrivate.post("DeleteSettings.php", { Source, Settings: settings });
      const result = response.data;
      if (result.status === 1) {
        pushToast("success", result.Success || "Layout reset to system defaults.");
        // Reset columns to defaults
        const defaultCols = columns.filter((c) => !c.hide).map((c) => c.field);
        setSelectedColumns(defaultCols);
        setColumnDefs(columns);
        gridRef.current?.api?.resetColumnState?.();
      } else {
        pushToast("error", "Error resetting settings.");
      }
    } catch (e) {
      console.error("Error resetting settings:", e);
      pushToast("error", "Error resetting settings.");
    }
  }, [axiosPrivate, Source, settings, columns, pushToast]);

  // ── Grid ready / settings application ───────────────────────────────────────

  const applySettings = useCallback((columnState, visibleColumns) => {
    const api = gridRef.current?.api;
    if (!api || !Array.isArray(columnState) || columnState.length === 0) return;

    const matchedDefs = columnState
      .map((field) => {
        const colDef = columns.find((c) => c.field === (field.colId || field.field));
        return colDef ? { ...colDef, width: field.width || colDef.width } : null;
      })
      .filter(Boolean)
      .filter((c) => !visibleColumns || visibleColumns.includes(c.field));

    // Only update column defs if we have valid matched columns
    if (matchedDefs.length > 0) {
      setColumnDefs(matchedDefs);
    }
  }, [columns]);

  const getdata = useCallback((params) => {
    if (Source) {
      axiosPrivate
        .post("GetSettings.php", { Source })
        .then((response) => {
          const result = response.data;
          if (result.status === 1) {
            setSettings(result.Settings);
            if (Array.isArray(result.Columns) && result.Columns.length > 0) {
              const matched = columns.filter((c) => result.Columns.includes(c.field));
              if (matched.length > 0) {
                setSelectedColumns(result.Columns);
                applySettings(result.Settings?.columnState, result.Columns);
              }
            }
          }
          onGridReady?.(params);
        })
        .catch((e) => {
          console.error("Error loading grid:", e);
          onGridReady?.(params);
        });
    } else {
      onGridReady?.(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosPrivate, Source, applySettings, onGridReady, columns]);

  const onBtnExport = useCallback(() => {
    gridRef.current?.api?.exportDataAsCsv({ fileName: `${Source}-${ExportFileName}` });
  }, [Source, ExportFileName]);

  // ── Column Filter Popover Helpers ──────────────────────────────────────────

  const filteredColumnsList = useMemo(() => {
    if (!columnSearch.trim()) return columns;
    const q = columnSearch.toLowerCase();
    return columns.filter((col) => {
      const label = col.headerName || col.field || "";
      return label.toLowerCase().includes(q);
    });
  }, [columns, columnSearch]);

  const handleToggleColumn = (field) => {
    setSelectedColumns((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const handleSelectAllColumns = () => {
    setSelectedColumns(columns.map((c) => c.field));
    setColumnDefs(columns);
  };

  const handleDeselectAllColumns = () => {
    const firstCol = columns[0] ? [columns[0].field] : [];
    setSelectedColumns(firstCol);
  };

  const handleResetColumns = () => {
    const defaultCols = columns.filter((c) => !c.hide).map((c) => c.field);
    setSelectedColumns(defaultCols);
    setColumnDefs(columns.filter((c) => defaultCols.includes(c.field)));
  };

  // ── Counts & Metrics ───────────────────────────────────────────────────────
  const rowCount = rowData?.length ?? 0;
  const filteredCount = filteredRowNodes?.length ?? rowCount;
  const isFiltered = hasActiveFilters && filteredCount !== rowCount;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        width: "100%",
        height: height || "600px",
        minHeight: 450,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: `1px solid ${alpha(muiTheme.palette.divider, 0.12)}`,
        bgcolor: isDark ? alpha(muiTheme.palette.background.paper, 0.7) : "#ffffff",
        overflow: "hidden",
        boxShadow: isDark
          ? "0 4px 20px rgba(0,0,0,0.35)"
          : "0 2px 14px rgba(0,0,0,0.03)",
      }}
    >
      {showHeaderItems && (
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.25,
            background: isDark
              ? alpha(muiTheme.palette.background.paper, 0.95)
              : "#ffffff",
            borderBottom: `1px solid ${alpha(muiTheme.palette.divider, 0.08)}`,
          }}
        >
          {/* ── Left Controls: Search, Clear Filters, Actions ─────────────── */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {QuickSearch && (
              <TextField
                size="small"
                placeholder={rowCount > 0 ? `Search ${rowCount} records…` : "Search records…"}
                value={searchText}
                onChange={onQuickFilterChanged}
                sx={{
                  width: { xs: 180, sm: 220, md: 250 },
                  "& .MuiOutlinedInput-root": {
                    height: 34,
                    fontSize: "0.8125rem",
                    borderRadius: 2,
                    bgcolor: isDark ? alpha("#fff", 0.04) : "#f8fafc",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      bgcolor: isDark ? alpha("#fff", 0.07) : "#f1f5f9",
                    },
                    "&.Mui-focused": {
                      bgcolor: isDark ? alpha("#fff", 0.08) : "#ffffff",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: "1.1rem", color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchText ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearQuickSearch} sx={{ p: 0.25 }}>
                        <Clear sx={{ fontSize: "0.95rem" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}

            {/* Active Filter Badge & Clear Action */}
            {hasActiveFilters && (
              <Tooltip title="Clear all active column filters and search query">
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<FilterAltOff sx={{ fontSize: "0.9rem !important" }} />}
                  onClick={resetFilters}
                  sx={{
                    fontSize: "0.75rem",
                    py: 0.4,
                    px: 1.25,
                    height: 32,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                  }}
                >
                  Clear Filters
                </Button>
              </Tooltip>
            )}

            {/* Contextual Action Buttons */}
            {addMember && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<Add sx={{ fontSize: "0.95rem !important" }} />}
                onClick={handleOpen}
                sx={{
                  fontSize: "0.75rem",
                  py: 0.4,
                  px: 1.5,
                  height: 32,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "none",
                }}
              >
                Add Member
              </Button>
            )}

            {addDiscussion && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<Add sx={{ fontSize: "0.95rem !important" }} />}
                onClick={handleOpen}
                sx={{
                  fontSize: "0.75rem",
                  py: 0.4,
                  px: 1.5,
                  height: 32,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "none",
                }}
              >
                Add Discussion
              </Button>
            )}
          </Stack>

          {/* ── Center Slot (Caller Provided Component) ───────────────────── */}
          <Box sx={{ flex: 1, minWidth: 0, px: 1, display: "flex", justifyContent: "center" }}>
            {headerComponent?.()}
          </Box>

          {/* ── Right Controls: Metrics, Columns, View Persistence, Export ── */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {/* Record Count Badge */}
            <Chip
              icon={isFiltered ? <FilterAlt sx={{ fontSize: "0.95rem !important" }} /> : undefined}
              label={
                isFiltered
                  ? `${filteredCount} of ${rowCount} records`
                  : `${rowCount} records`
              }
              size="small"
              color={isFiltered ? "primary" : "default"}
              variant={isFiltered ? "filled" : "outlined"}
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                height: 28,
                borderRadius: 1.5,
                bgcolor: isFiltered
                  ? alpha(muiTheme.palette.primary.main, 0.12)
                  : alpha(muiTheme.palette.text.primary, 0.04),
                color: isFiltered ? "primary.main" : "text.primary",
                border: isFiltered
                  ? `1px solid ${alpha(muiTheme.palette.primary.main, 0.3)}`
                  : undefined,
              }}
            />

            {/* Customise Columns Popover Trigger */}
            {ChooseColumns && (
              <>
                <Tooltip title="Select visible columns">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ViewColumn sx={{ fontSize: "1rem !important" }} />}
                    endIcon={<KeyboardArrowDown sx={{ fontSize: "0.9rem !important" }} />}
                    onClick={handleColumnsOpen}
                    sx={{
                      fontSize: "0.75rem",
                      py: 0.4,
                      px: 1.25,
                      height: 32,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: alpha(muiTheme.palette.divider, 0.2),
                    }}
                  >
                    Columns
                  </Button>
                </Tooltip>

                {/* Material-UI Columns Popover */}
                <Popover
                  open={Boolean(columnsAnchor)}
                  anchorEl={columnsAnchor}
                  onClose={handleColumnsClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: {
                      width: 290,
                      maxHeight: 440,
                      p: 1.5,
                      borderRadius: 2.5,
                      boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
                      border: `1px solid ${alpha(muiTheme.palette.divider, 0.12)}`,
                    },
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Customise Columns
                      </Typography>
                      <Chip
                        label={`${selectedColumns.length}/${columns.length}`}
                        size="small"
                        color="primary"
                        sx={{ height: 20, fontSize: "0.72rem", fontWeight: 700 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Toggle visibility of grid data fields
                    </Typography>
                  </Box>

                  {/* Search filter for columns */}
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search columns…"
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: 16, color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                      endAdornment: columnSearch ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setColumnSearch("")}>
                            <Clear sx={{ fontSize: 14 }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                      sx: { height: 32, fontSize: "0.8rem", borderRadius: 1.5, mb: 1 },
                    }}
                  />

                  {/* Quick selection actions */}
                  <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
                    <Button size="small" variant="text" onClick={handleSelectAllColumns} sx={{ fontSize: "0.72rem", py: 0.2, px: 0.8, textTransform: "none" }}>
                      Show All
                    </Button>
                    <Button size="small" variant="text" onClick={handleDeselectAllColumns} sx={{ fontSize: "0.72rem", py: 0.2, px: 0.8, textTransform: "none" }}>
                      Hide All
                    </Button>
                    <Button size="small" variant="text" onClick={handleResetColumns} sx={{ fontSize: "0.72rem", py: 0.2, px: 0.8, ml: "auto", textTransform: "none", color: "text.secondary" }}>
                      Reset
                    </Button>
                  </Box>

                  <Divider sx={{ my: 0.75 }} />

                  {/* Scrollable Column List */}
                  <Box sx={{ maxHeight: 240, overflowY: "auto", pr: 0.5 }}>
                    {filteredColumnsList.map((col) => {
                      const isChecked = selectedColumns.includes(col.field);
                      return (
                        <Box
                          key={col.field}
                          onClick={() => handleToggleColumn(col.field)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            py: 0.4,
                            px: 0.8,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            transition: "background 0.1s ease",
                            "&:hover": { bgcolor: alpha(muiTheme.palette.primary.main, 0.08) },
                          }}
                        >
                          <Checkbox size="small" checked={isChecked} sx={{ p: 0.4, mr: 0.8 }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8125rem",
                              fontWeight: isChecked ? 600 : 400,
                              color: isChecked ? "text.primary" : "text.secondary",
                            }}
                          >
                            {col.headerName || col.field}
                          </Typography>
                        </Box>
                      );
                    })}
                    {filteredColumnsList.length === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", py: 2 }}>
                        No columns match search
                      </Typography>
                    )}
                  </Box>
                </Popover>
              </>
            )}

            {/* Layout Persistence Group */}
            <Tooltip title="Save current column order, widths & visibility">
              <Button
                size="small"
                variant="outlined"
                startIcon={<BookmarkBorder sx={{ fontSize: "0.95rem !important" }} />}
                onClick={SaveSettings}
                sx={{
                  fontSize: "0.75rem",
                  py: 0.4,
                  px: 1.25,
                  height: 32,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: alpha(muiTheme.palette.divider, 0.2),
                }}
              >
                Save View
              </Button>
            </Tooltip>

            <Tooltip title="Reset saved layout back to system defaults">
              <IconButton
                size="small"
                onClick={deleteSettings}
                sx={{
                  height: 32,
                  width: 32,
                  borderRadius: 2,
                  border: `1px solid ${alpha(muiTheme.palette.divider, 0.2)}`,
                  color: "text.secondary",
                  "&:hover": { color: "warning.main", borderColor: "warning.main" },
                }}
              >
                <RestoreFromTrash sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </Tooltip>

            {/* Export CSV Button */}
            {exportAllowed && (
              <Tooltip title="Export current grid records to CSV">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownload sx={{ fontSize: "0.95rem !important" }} />}
                  onClick={onBtnExport}
                  sx={{
                    fontSize: "0.75rem",
                    py: 0.4,
                    px: 1.25,
                    height: 32,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: alpha(muiTheme.palette.divider, 0.2),
                  }}
                >
                  Export
                </Button>
              </Tooltip>
            )}

            {/* Refresh Data Button */}
            <Tooltip title="Reload table data">
              <IconButton
                size="small"
                onClick={onGridReady}
                sx={{
                  height: 32,
                  width: 32,
                  borderRadius: 2,
                  bgcolor: alpha(muiTheme.palette.primary.main, 0.08),
                  color: "primary.main",
                  "&:hover": { bgcolor: alpha(muiTheme.palette.primary.main, 0.18) },
                }}
              >
                {showLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Refresh sx={{ fontSize: "1.1rem" }} />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      )}

      {/* ── AG Grid Body ────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minHeight: 400, height: "100%", width: "100%" }} className={theme}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          autoSizeStrategy={autoSizeStrategy}
          getRowStyle={getRowStyle}
          rowSelection="single"
          pagination={true}
          onCellClicked={onRowSelected}
          onCellValueChanged={onCellValueChanged}
          onGridReady={getdata}
          onFilterChanged={updateFilteredRowNodes}
          onColumnMoved={(e) => {
            const api = e.api;
            const columnState = api?.getColumnState ? api.getColumnState() : [];
            setSettings({ columnState });
          }}
          onColumnResized={(e) => {
            if (e.finished) {
              const api = e.api;
              const columnState = api?.getColumnState ? api.getColumnState() : [];
              setSettings({ columnState });
            }
          }}
          overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading data…</span>'
          overlayNoRowsTemplate='<span class="ag-overlay-loading-center">No records found</span>'
        />
      </Box>
    </Box>
  );
}

// ─── PropTypes & Defaults ─────────────────────────────────────────────────────

AgGrid.propTypes = {
  columns: PropTypes.array,
  rowData: PropTypes.array,
  onGridReady: PropTypes.func,
  ExportFileName: PropTypes.string,
  GoTo: PropTypes.string,
  showHeaderItems: PropTypes.bool,
  passdata: PropTypes.bool,
  QuickSearch: PropTypes.bool,
  exportAllowed: PropTypes.bool,
  ChooseColumns: PropTypes.bool,
  showLoading: PropTypes.bool,
  addMember: PropTypes.bool,
  addDiscussion: PropTypes.bool,
  handleOpen: PropTypes.func,
  onRowSelectedData: PropTypes.func,
  handleViewModalOpen: PropTypes.func,
  onDataChange: PropTypes.func,
  headerComponent: PropTypes.func,
  setFilteredRows: PropTypes.func,
  Source: PropTypes.string,
  settingsName: PropTypes.string,
  theme: PropTypes.string,
  setRowStyle: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

AgGrid.defaultProps = {
  onRowSelectedData: () => {},
  handleViewModalOpen: () => {},
  onDataChange: () => {},
  headerComponent: () => null,
  setFilteredRows: () => {},
  showHeaderItems: true,
  QuickSearch: true,
  exportAllowed: true,
  ChooseColumns: true,
  showLoading: false,
  addMember: false,
  addDiscussion: false,
  Source: "Followup",
  theme: "ag-theme-alpine",
  setRowStyle: false,
};
